import { useOktaAuth } from "@okta/okta-react";
import { useCallback, useMemo } from "react";
import { AUTH_BYPASS, BYPASS_USER } from "@/auth-bypass";
import { useLogoutLoading } from "../contexts/logout-loading-context";

export interface AuthState {
	isAuthenticated: boolean;
	isLoading: boolean;
	user: {
		email?: string;
		name?: string;
		sub?: string;
	} | null;
}

export interface AuthActions {
	login: (redirectUrl?: string) => Promise<void>;
	logout: () => Promise<void>;
}

export const useAuth = (): AuthState & AuthActions => {
	const { authState, oktaAuth } = useOktaAuth();
	const { setIsLoggingOut } = useLogoutLoading();

	const user = useMemo(() => {
		if (AUTH_BYPASS) {
			return BYPASS_USER;
		}

		if (!authState?.isAuthenticated || !authState.idToken) {
			return null;
		}

		return {
			email: authState.idToken.claims.email as string | undefined,
			name: authState.idToken.claims.name as string | undefined,
			sub: authState.idToken.claims.sub as string | undefined,
		};
	}, [authState]);

	const login = useCallback(
		async (redirectUrl = "/tenants") => {
			if (AUTH_BYPASS) {
				return;
			}

			await oktaAuth.signInWithRedirect({
				originalUri: redirectUrl,
			});
		},
		[oktaAuth],
	);

	const logout = useCallback(async () => {
		if (AUTH_BYPASS) {
			return;
		}

		setIsLoggingOut(true);
		await oktaAuth.signOut();
	}, [oktaAuth, setIsLoggingOut]);

	return {
		isAuthenticated: AUTH_BYPASS || (authState?.isAuthenticated ?? false),
		isLoading: !AUTH_BYPASS && authState === null,
		user,
		login,
		logout,
	};
};
